// 그룹 동영상 URL 목록(groupVideos) — 모든 스킬, 여러 개, 항목은 { url } (docs/문제은행_시스템.md 「1. QuestionGroup › 그룹 동영상」).
// 성취도 백엔드는 2026-10-12 부터 그룹·검색·시험 스냅샷 응답에 항상 배열로 주지만, 그 전에 만든 시험의 스냅샷은 키가 없고
// 그룹 응답의 questions[].groupVideos 는 null 일 수 있어, 읽는 쪽은 모두 이 함수로 { url } 목록으로 맞춘다
// (문제은행 폼·목록·상세, 검색 응답 매핑, 시험 생성 마법사 미리보기, 문제 선택 상세, 시험 미리보기 공용).
// 강사 화면 src/utils/groupVideos.js 와 같은 로직이다(2026-10-12 강사 화면팀 전달, 2026-10-13 YouTube 만으로 좁힘).
export const normalizeGroupVideos = (list) => {
  if (!Array.isArray(list)) return []
  return list
    .map(item => {
      if (typeof item === 'string') return { url: item.trim() }
      if (item && typeof item === 'object') return { ...item, url: String(item.url || '').trim() }
      return null
    })
    .filter(item => item && item.url)
}

// http(s) 절대 주소만 — 성취도 백엔드 검증과 같은 기준이고, javascript: 같은 주소를 <a href> 로 그리지 않기 위한 판정.
// 등록은 아래 isYoutubeVideoUrl 로 더 좁힌다 (YouTube 만, 2026-10-13 결정).
export const isValidVideoUrl = (url) => {
  const value = String(url || '').trim()
  if (!value) return false
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

// YouTube 동영상 ID — watch?v=ID · youtu.be/ID · shorts/ID · embed/ID · live/ID (www.·m.·youtube-nocookie.com 포함). 아니면 ''
const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/
export const getYoutubeVideoId = (url) => {
  const value = String(url || '').trim()
  if (!isValidVideoUrl(value)) return ''
  const parsed = new URL(value)
  const host = parsed.hostname.toLowerCase().replace(/^(www|m)\./, '')
  const segments = parsed.pathname.split('/').filter(Boolean)
  let id = ''
  if (host === 'youtu.be') {
    id = segments[0] || ''
  } else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    if (segments[0] === 'watch') id = parsed.searchParams.get('v') || ''
    else if (['shorts', 'embed', 'live', 'v'].includes(segments[0])) id = segments[1] || ''
  }
  return YOUTUBE_ID.test(id) ? id : ''
}

// 등록할 수 있는 주소 — YouTube 만 (2026-10-13 결정, 우선 YouTube 만 쓰기로). 폼 검증·그룹 검증·재생 버튼 판정이 같이 쓴다.
// 백엔드 검증은 http(s) 그대로라 이 제한은 화면 규칙이다(강사 화면과 같은 규칙).
export const isYoutubeVideoUrl = (url) => getYoutubeVideoId(url) !== ''

// "90" · "1m30s" · "1h2m3s" → 초. 못 읽으면 0
const parseStartSeconds = (value) => {
  const text = String(value || '').trim()
  if (!text) return 0
  if (/^\d+$/.test(text)) return Number(text)
  const match = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/.exec(text)
  if (!match) return 0
  return Number(match[1] || 0) * 3600 + Number(match[2] || 0) * 60 + Number(match[3] || 0)
}

// 플레이어 종류 — VideoEmbed.vue 가 쓴다.
//   youtube: YouTube 플레이어(iframe 임베드). 쿼리 t/start 는 시작 초로
//   link:    그 밖의 http(s) 주소(예전 데이터·다른 경로로 들어온 값) — 화면 안에서 재생하지 않고 링크만
//   invalid: http(s) 가 아닌 값 — 아무것도 그리지 않는다
export const getVideoEmbed = (url) => {
  const value = String(url || '').trim()
  if (!isValidVideoUrl(value)) return { kind: 'invalid', url: value, id: '', src: '' }
  const id = getYoutubeVideoId(value)
  if (!id) return { kind: 'link', url: value, id: '', src: '' }
  const params = new URL(value).searchParams
  const start = parseStartSeconds(params.get('start') || params.get('t'))
  const src = `https://www.youtube.com/embed/${id}?rel=0${start > 0 ? `&start=${start}` : ''}`
  return { kind: 'youtube', url: value, id, src }
}
